import { createTheme, Theme } from '@mui/material/styles';

const displayFont = "'Manrope', sans-serif";
const bodyFont = "'Inter', sans-serif";

const createAppTheme = (mode: 'light' | 'dark'): Theme => {
  const isDark = mode === 'dark';

  const brand = {
    primary: isDark ? '#a6c8ff' : '#005eb1',
    primaryContainer: '#4197ff',
    secondary: isDark ? '#a3cfcc' : '#3c6564',
    tertiary: isDark ? '#00e1ab' : '#8f4e00',
    error: '#ffb4ab',
  };

  const colors = {
    dark: {
      bgDefault: '#05070a', // surface
      bgPaper: '#111417', // surface_container_low
      bgHover: '#191c1f', // surface_container
      bgElevated: '#1d2023', // surface_container_highest
      bgSubtle: '#020305',
      border: 'rgba(255, 255, 255, 0.1)', // outline_variant/catchlight
      borderStrong: 'rgba(255, 255, 255, 0.15)',
      borderHover: 'rgba(255, 255, 255, 0.25)',
      textPrimary: '#fdfdfd',
      textSecondary: '#a0a8a8',
      textDisabled: '#404545',
      textMuted: '#606868',
      tableHead: '#05070a',
      chipBg: '#191c1f',
      inputBg: '#020305', // surface_container_lowest
      gloss: 'inset 0 1px 0 rgba(255,255,255,0.1)',
      glossStrong: 'inset 0 1px 0 rgba(255,255,255,0.15)',
    },
    light: {
      bgDefault: '#f7fafd',
      bgPaper: '#ffffff',
      bgHover: '#f1f4f7',
      bgElevated: '#ffffff',
      bgSubtle: '#f4f6f8',
      border: 'rgba(24, 28, 30, 0.15)',
      borderStrong: 'rgba(24, 28, 30, 0.25)',
      borderHover: 'rgba(24, 28, 30, 0.40)',
      textPrimary: '#181c1e',
      textSecondary: '#40484c',
      textDisabled: '#8c9498',
      textMuted: '#647074',
      tableHead: '#f7fafd',
      chipBg: '#f1f4f7',
      inputBg: '#ffffff',
      gloss: 'inset 0 1px 0 rgba(255,255,255,0.85)',
      glossStrong: 'inset 0 1px 0 rgba(255,255,255,1)',
    },
  };

  const c = isDark ? colors.dark : colors.light;

  return createTheme({
    palette: createPaletteOptions(isDark, mode, brand, c),
    typography: createTypographyOptions(),
    shape: { borderRadius: 6 }, // slightly tighter for monolith feel
    components: createComponentOverrides(isDark, brand, c),
  });
};

const createPaletteOptions = (
  isDark: boolean,
  mode: 'light' | 'dark',
  brand: {
    primary: string;
    primaryContainer: string;
    secondary: string;
    tertiary: string;
    error: string;
  },
  c: {
    bgDefault: string;
    bgPaper: string;
    textPrimary: string;
    textSecondary: string;
    textDisabled: string;
    border: string;
  },
) => ({
  mode,
  background: {
    default: c.bgDefault,
    paper: c.bgPaper,
  },
  primary: {
    main: brand.primary,
    light: '#c8ebe9',
    dark: '#7cafac',
  },
  secondary: { main: brand.secondary },
  warning: { main: '#f59e0b' },
  success: { main: brand.tertiary },
  error: { main: brand.error },
  info: { main: brand.secondary },
  text: {
    primary: c.textPrimary,
    secondary: c.textSecondary,
    disabled: c.textDisabled,
  },
  divider: c.border,
});

const createTypographyOptions = () => ({
  fontFamily: bodyFont,
  fontSize: 14,
  fontWeightLight: 400,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightBold: 600,
  h1: {
    fontFamily: displayFont,
    fontWeight: 800,
    letterSpacing: '-0.02em',
    lineHeight: 1.1,
  },
  h2: {
    fontFamily: displayFont,
    fontWeight: 800,
    letterSpacing: '-0.02em',
    lineHeight: 1.15,
  },
  h3: {
    fontFamily: displayFont,
    fontWeight: 600,
    letterSpacing: '-0.02em',
    lineHeight: 1.2,
  },
  h4: { fontFamily: displayFont, fontWeight: 600, letterSpacing: '-0.01em' },
  h5: { fontFamily: displayFont, fontWeight: 600 },
  h6: { fontFamily: displayFont, fontWeight: 600 },
  subtitle1: {
    fontFamily: bodyFont,
    fontWeight: 500,
    letterSpacing: '-0.005em',
  },
  subtitle2: { fontFamily: bodyFont, fontWeight: 500 },
  body1: {
    fontFamily: bodyFont,
    fontWeight: 400,
    lineHeight: 1.6,
    letterSpacing: '-0.01em',
  },
  body2: {
    fontFamily: bodyFont,
    fontWeight: 400,
    lineHeight: 1.5,
    fontSize: '0.875rem',
  },
  button: {
    fontFamily: bodyFont,
    fontWeight: 500,
    textTransform: 'none' as const,
    letterSpacing: '0',
  },
  caption: { fontFamily: bodyFont, fontWeight: 400, fontSize: '0.6875rem' },
  overline: {
    fontFamily: displayFont,
    fontWeight: 600,
    letterSpacing: '0.08em',
    fontSize: '0.625rem',
  },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createComponentOverrides = (isDark: boolean, brand: any, c: any) => ({
  ...createLayoutOverrides(isDark, c),
  ...createTableOverrides(isDark, brand, c),
  ...createInputOverrides(isDark, brand, c),
  ...createMiscOverrides(isDark, brand, c),
  ...createAlertOverrides(isDark, c),
  ...createDialogOverrides(isDark, c),
  ...createWidgetOverrides(isDark, c),
  ...createBadgeOverrides(isDark, brand),
  ...createFormOverrides(isDark, brand, c),
  ...createNavOverrides(isDark, brand, c),
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createLayoutOverrides = (isDark: boolean, c: any) => ({
  MuiCssBaseline: {
    styleOverrides: `
          @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&family=Inter:wght@400;500;600&display=swap');
          *, *::before, *::after {
            font-family: ${bodyFont};
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            box-sizing: border-box;
          }
          body {
            background: ${c.bgDefault};
            background-attachment: fixed;
            color: ${c.textPrimary};
            margin: 0;
            padding: 0;
          }
          ::-webkit-scrollbar { width: 6px; height: 6px; }
          ::-webkit-scrollbar-track { background: transparent; }
          ::-webkit-scrollbar-thumb {
            background: ${isDark ? '#2a2a2a' : '#e0e0e0'};
            border-radius: 99px;
          }
          ::-webkit-scrollbar-thumb:hover {
            background: ${isDark ? '#3a3a3a' : '#cccccc'};
          }
        `,
  },

  MuiPaper: {
    styleOverrides: {
      root: {
        fontFamily: bodyFont,
        backgroundImage: isDark
          ? 'linear-gradient(145deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0) 100%)'
          : 'linear-gradient(145deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0) 100%)',
        backgroundColor: isDark
          ? 'rgba(17,17,17,0.7)'
          : 'rgba(255,255,255,0.7)',
        backdropFilter: 'blur(16px)',
        border: `1px solid ${c.border}`,
        boxShadow: isDark
          ? `0 32px 64px rgba(0,0,0,0.08), ${c.gloss}`
          : `0 4px 16px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.85)`,
      },
      elevation1: {
        boxShadow: isDark
          ? `0 32px 64px rgba(0,0,0,0.08), ${c.gloss}`
          : '0 8px 32px rgba(0,0,0,0.08), 0 2px 4px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,0.85)',
      },
      elevation2: {
        boxShadow: isDark
          ? `0 32px 64px rgba(0,0,0,0.1), ${c.gloss}`
          : '0 16px 48px rgba(0,0,0,0.1), 0 4px 8px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,1)',
      },
    },
  },

  MuiCard: {
    styleOverrides: {
      root: {
        fontFamily: bodyFont,
        backgroundImage: isDark
          ? 'linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0) 100%)'
          : 'linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0) 100%)',
        backgroundColor: isDark
          ? 'rgba(20,20,20,0.7)'
          : 'rgba(255,255,255,0.7)',
        backdropFilter: 'blur(16px)',
        border: `1px solid ${c.border}`,
        borderRadius: 12,
        boxShadow: isDark
          ? `0 32px 64px rgba(0,0,0,0.06), ${c.gloss}`
          : `0 2px 12px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04), inset 0 1px 0 rgba(255,255,255,1)`,
        transition:
          'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
        '&:hover': {
          borderColor: c.borderHover,
          boxShadow: isDark
            ? `0 8px 32px rgba(0,0,0,0.7), ${c.glossStrong}`
            : `0 8px 24px rgba(0,0,0,0.08), 0 2px 8px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,1)`,
          transform: 'translateY(-1px)',
        },
      },
    },
  },

  MuiCardContent: {
    styleOverrides: {
      root: { fontFamily: bodyFont },
    },
  },

  MuiCardHeader: {
    styleOverrides: {
      root: { fontFamily: bodyFont },
      title: { fontFamily: bodyFont, fontWeight: 600, fontSize: 14 },
      subheader: {
        fontFamily: bodyFont,
        fontSize: 12,
        color: c.textSecondary,
      },
    },
  },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createTableOverrides = (isDark: boolean, brand: any, c: any) => ({
  MuiTableContainer: {
    styleOverrides: {
      root: {
        border: `1px solid ${c.border}`,
        borderRadius: 10,
        overflow: 'hidden',
      },
    },
  },

  MuiTable: {
    styleOverrides: {
      root: { fontFamily: bodyFont },
    },
  },

  MuiTableCell: {
    styleOverrides: {
      root: {
        fontFamily: bodyFont,
        borderBottom: `1px solid ${c.border}`,
        padding: '11px 16px',
        color: c.textPrimary,
        fontSize: 13,
      },
      head: {
        fontFamily: bodyFont,
        color: c.textSecondary,
        fontSize: 11,
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.07em',
        backgroundColor: c.tableHead,
        borderBottom: `1px solid ${c.border}`,
        whiteSpace: 'nowrap',
      },
    },
  },

  MuiTableRow: {
    styleOverrides: {
      root: {
        transition: 'background-color 0.1s ease',
        '&:hover': { backgroundColor: c.bgHover },
        '&:last-child td': { borderBottom: 0 },
      },
    },
  },

  MuiTablePagination: {
    styleOverrides: {
      root: {
        fontFamily: bodyFont,
        fontSize: 13,
        borderTop: `1px solid ${c.border}`,
      },
      selectLabel: { fontFamily: bodyFont, fontSize: 12 },
      displayedRows: { fontFamily: bodyFont, fontSize: 12 },
      select: { fontFamily: bodyFont },
    },
  },

  MuiTabs: {
    styleOverrides: {
      root: { borderBottom: `1px solid ${c.border}`, minHeight: 44 },
      indicator: {
        backgroundColor: isDark ? '#ffffff' : brand.primary,
        height: 1.5,
        borderRadius: 99,
      },
    },
  },

  MuiTab: {
    styleOverrides: {
      root: {
        fontFamily: bodyFont,
        textTransform: 'none',
        fontWeight: 400,
        fontSize: 13,
        minHeight: 44,
        color: c.textSecondary,
        transition: 'color 0.15s ease',
        '&.Mui-selected': {
          color: isDark ? '#ffffff' : brand.primary,
          fontWeight: 500,
        },
        '&:hover': {
          color: isDark ? '#cccccc' : brand.primary,
          backgroundColor: c.bgHover,
        },
      },
    },
  },

  MuiChip: {
    styleOverrides: {
      root: {
        fontFamily: bodyFont,
        fontWeight: 500,
        fontSize: 12,
        height: 24,
        borderRadius: 6,
        letterSpacing: '0.01em',
        transition: 'filter 0.15s ease, box-shadow 0.15s ease',
        background: isDark
          ? 'linear-gradient(145deg, #1e1e1e 0%, #181818 100%)'
          : 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)',
        border: `1px solid ${isDark ? '#2e2e2e' : '#e0e0e0'}`,
        color: isDark ? '#999999' : '#666666',
        boxShadow: isDark ? c.gloss : `0 1px 2px rgba(0,0,0,0.05), ${c.gloss}`,
        '&:hover': { filter: 'brightness(1.08)' },
        '& .MuiChip-label': { paddingLeft: 10, paddingRight: 10 },
        '& .MuiChip-icon': { fontSize: 13, marginLeft: 7, marginRight: -3 },
      },
      colorPrimary: isDark
        ? {
            background: 'linear-gradient(145deg, #0e2d2c 0%, #091f1e 100%)',
            border: '1px solid #154340',
            color: '#5fffb0',
            boxShadow: 'inset 0 1px 0 rgba(95,255,176,0.08)',
          }
        : {
            background: 'linear-gradient(145deg, #e6f4f3 0%, #d8edec 100%)',
            border: '1px solid #a8d5d3',
            color: brand.primary,
            boxShadow: `0 1px 2px ${'rgba(163, 207, 204, 0.15)'}, ${c.gloss}`,
          },
      colorSecondary: isDark
        ? {
            background: 'linear-gradient(145deg, #0c1e38 0%, #091629 100%)',
            border: '1px solid #153155',
            color: brand.secondary,
            boxShadow: 'inset 0 1px 0 rgba(109,184,255,0.07)',
          }
        : {
            background: 'linear-gradient(145deg, #eaf3ff 0%, #ddeeff 100%)',
            border: '1px solid #aeccff',
            color: brand.secondary,
            boxShadow: `0 1px 2px ${'rgba(166, 200, 255, 0.15)'}, ${c.gloss}`,
          },
      colorSuccess: isDark
        ? {
            background: 'linear-gradient(145deg, #0a2218 0%, #071a12 100%)',
            border: '1px solid #123d25',
            color: '#4ade80',
            boxShadow: 'inset 0 1px 0 rgba(74,222,128,0.07)',
          }
        : {
            background: 'linear-gradient(145deg, #eaf7f0 0%, #ddf2e8 100%)',
            border: '1px solid #9ddcbb',
            color: '#166534',
            boxShadow: `0 1px 2px rgba(16,185,129,0.1), ${c.gloss}`,
          },
      colorError: isDark
        ? {
            background: 'linear-gradient(145deg, #2a0e0e 0%, #1f0909 100%)',
            border: '1px solid #4a1515',
            color: '#f87171',
            boxShadow: 'inset 0 1px 0 rgba(248,113,113,0.07)',
          }
        : {
            background: 'linear-gradient(145deg, #fef2f2 0%, #fee8e8 100%)',
            border: '1px solid #fca5a5',
            color: '#991b1b',
            boxShadow: `0 1px 2px rgba(239,68,68,0.1), ${c.gloss}`,
          },
      colorWarning: isDark
        ? {
            background: 'linear-gradient(145deg, #281a04 0%, #1e1302 100%)',
            border: '1px solid #453008',
            color: '#fbbf24',
            boxShadow: 'inset 0 1px 0 rgba(251,191,36,0.07)',
          }
        : {
            background: 'linear-gradient(145deg, #fffbeb 0%, #fff5d6 100%)',
            border: '1px solid #fcd34d',
            color: '#92400e',
            boxShadow: `0 1px 2px rgba(245,158,11,0.1), ${c.gloss}`,
          },
      colorInfo: isDark
        ? {
            background: 'linear-gradient(145deg, #0c1e38 0%, #091629 100%)',
            border: '1px solid #153155',
            color: '#60a5fa',
            boxShadow: 'inset 0 1px 0 rgba(96,165,250,0.07)',
          }
        : {
            background: 'linear-gradient(145deg, #eff6ff 0%, #e5f0ff 100%)',
            border: '1px solid #93c5fd',
            color: '#1e40af',
            boxShadow: `0 1px 2px rgba(59,130,246,0.1), ${c.gloss}`,
          },
      sizeSmall: {
        height: 20,
        fontSize: 11,
        borderRadius: 5,
        '& .MuiChip-label': { paddingLeft: 8, paddingRight: 8 },
      },
    },
  },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createInputOverrides = (isDark: boolean, brand: any, c: any) => ({
  MuiButton: {
    styleOverrides: {
      root: {
        fontFamily: bodyFont,
        textTransform: 'none',
        fontWeight: 600,
        fontSize: 14,
        borderRadius: 12,
        letterSpacing: '0.02em',
        transition: 'all 0.2s ease',
        boxShadow: 'none',
        '&:active': { transform: 'scale(0.98)' },
        '&:hover': { boxShadow: 'none' },
      },
      containedPrimary: {
        background: isDark
          ? `linear-gradient(135deg, ${brand.primary} 0%, ${brand.primaryContainer} 100%)`
          : `linear-gradient(180deg, ${brand.primaryContainer} 0%, #3080e8 100%)`,
        color: isDark ? '#05070a' : '#002f5d',
        border: 'none',
        boxShadow: isDark
          ? 'inset 0 1px 0 rgba(255,255,255,0.4), 0 8px 16px rgba(166, 200, 255, 0.15)'
          : 'inset 0 1px 0 rgba(255,255,255,0.4), 0 8px 16px rgba(0, 94, 177, 0.2)',
        '&:hover': {
          background:
            'linear-gradient(135deg, ' +
            brand.primary +
            ' 0%, ' +
            brand.primaryContainer +
            ' 100%)',
          filter: 'brightness(1.15)',
          boxShadow: isDark
            ? 'inset 0 1px 0 rgba(255,255,255,0.6), 0 8px 24px rgba(166, 200, 255, 0.25)'
            : 'inset 0 1px 0 rgba(255,255,255,0.85), 0 8px 24px rgba(0, 94, 177, 0.35)',
        },
      },
      containedSecondary: {
        background: `linear-gradient(145deg, ${brand.secondary} 0%, ${brand.secondary} 100%)`,
        color: '#ffffff',
        border: `1px solid ${brand.secondary}`,
        boxShadow: `inset 0 1px 0 rgba(255,255,255,0.15), 0 1px 3px ${'rgba(166, 200, 255, 0.15)'}`,
        '&:hover': {
          background: `linear-gradient(145deg, #80bbff 0%, ${brand.secondary} 100%)`,
          boxShadow: `inset 0 1px 0 rgba(255,255,255,0.15), 0 2px 8px ${'rgba(166, 200, 255, 0.15)'}`,
        },
      },
      outlinedPrimary: {
        background: isDark
          ? 'linear-gradient(145deg, #1e1e1e 0%, #161616 100%)'
          : 'linear-gradient(145deg, #ffffff 0%, #f7f7f7 100%)',
        borderColor: c.borderStrong,
        color: c.textPrimary,
        boxShadow: isDark ? c.gloss : `0 1px 2px rgba(0,0,0,0.05), ${c.gloss}`,
        '&:hover': {
          borderColor: isDark ? '#555555' : brand.primary,
          backgroundColor: 'transparent',
          background: isDark
            ? 'linear-gradient(145deg, #242424 0%, #1a1a1a 100%)'
            : 'linear-gradient(145deg, #f7f7f7 0%, #f0f0f0 100%)',
        },
      },
      text: {
        color: c.textSecondary,
        '&:hover': {
          backgroundColor: c.bgHover,
          color: c.textPrimary,
        },
      },
    },
  },

  MuiIconButton: {
    styleOverrides: {
      root: {
        color: c.textSecondary,
        borderRadius: 7,
        transition: 'all 0.15s ease',
        '&:hover': {
          color: c.textPrimary,
          backgroundColor: c.bgHover,
        },
        '&:active': { transform: 'scale(0.94)' },
      },
    },
  },

  MuiTextField: {
    styleOverrides: {
      root: { fontFamily: bodyFont },
    },
  },

  MuiInputBase: {
    styleOverrides: {
      root: {
        fontFamily: bodyFont,
        fontSize: 13,
        backgroundColor: c.inputBg,
      },
      input: {
        fontFamily: bodyFont,
        '&::placeholder': { color: c.textMuted, opacity: 1 },
      },
    },
  },

  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: 7,
        background: isDark
          ? 'linear-gradient(145deg, #111111 0%, #0d0d0d 100%)'
          : 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
        boxShadow: isDark ? c.gloss : `0 1px 2px rgba(0,0,0,0.04), ${c.gloss}`,
        transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: c.border,
          transition: 'border-color 0.15s ease',
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
          borderColor: c.borderStrong,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
          borderColor: isDark ? '#ffffff' : brand.primary,
          borderWidth: 1,
        },
        '&.Mui-focused': {
          boxShadow: isDark
            ? `0 0 0 3px rgba(255,255,255,0.06), ${c.glossStrong}`
            : `0 0 0 3px ${'rgba(163, 207, 204, 0.15)'}, ${c.glossStrong}`,
        },
      },
    },
  },

  MuiSelect: {
    styleOverrides: {
      select: { fontFamily: bodyFont, fontSize: 13 },
      icon: { color: c.textSecondary },
    },
  },

  MuiMenuItem: {
    styleOverrides: {
      root: {
        fontFamily: bodyFont,
        fontSize: 13,
        borderRadius: 5,
        margin: '1px 4px',
        padding: '7px 10px',
        transition: 'background-color 0.1s ease',
        '&:hover': { backgroundColor: c.bgHover },
        '&.Mui-selected': {
          backgroundColor: isDark ? '#1e1e1e' : '#f0f0f0',
          fontWeight: 500,
          '&:hover': { backgroundColor: c.bgHover },
        },
      },
    },
  },

  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: 9,
        border: `1px solid ${c.border}`,
        background: isDark
          ? 'linear-gradient(145deg, #181818 0%, #111111 100%)'
          : 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
        boxShadow: isDark
          ? `0 8px 32px rgba(0,0,0,0.8), ${c.glossStrong}`
          : `0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06), ${c.glossStrong}`,
        padding: '4px 0',
      },
    },
  },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createMiscOverrides = (isDark: boolean, brand: any, c: any) => ({
  MuiLinearProgress: {
    styleOverrides: {
      root: {
        borderRadius: 3,
        backgroundColor: isDark ? '#222222' : '#ebebeb',
        height: 5,
        overflow: 'hidden',
        boxShadow: isDark
          ? 'inset 0 1px 2px rgba(0,0,0,0.5)'
          : 'inset 0 1px 2px rgba(0,0,0,0.08)',
      },
      bar: {
        borderRadius: 3,
        background: isDark
          ? `linear-gradient(90deg, ${brand.secondary} 0%, ${brand.secondary} 100%)`
          : `linear-gradient(90deg, ${brand.primary} 0%, ${brand.primary} 100%)`,
      },
    },
  },

  MuiCircularProgress: {
    styleOverrides: {
      colorPrimary: {
        color: isDark ? brand.secondary : brand.primary,
      },
    },
  },

  MuiDivider: {
    styleOverrides: { root: { borderColor: c.border } },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: {
        fontFamily: bodyFont,
        fontSize: 12,
        fontWeight: 400,
        padding: '6px 10px',
        borderRadius: 6,
        backgroundColor: isDark ? '#2a2a2a' : '#111111',
        border: `1px solid ${isDark ? '#3a3a3a' : 'transparent'}`,
        color: '#ffffff',
        boxShadow: isDark
          ? `0 4px 16px rgba(0,0,0,0.8), ${c.gloss}`
          : '0 4px 16px rgba(0,0,0,0.3)',
      },
      arrow: {
        color: isDark ? '#2a2a2a' : '#111111',
      },
    },
  },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createAlertOverrides = (isDark: boolean, c: any) => ({
  MuiAlert: {
    styleOverrides: {
      root: {
        fontFamily: bodyFont,
        fontSize: 13,
        borderRadius: 8,
        border: `1px solid ${c.border}`,
        boxShadow: isDark ? c.gloss : `0 1px 3px rgba(0,0,0,0.05), ${c.gloss}`,
      },
      standardSuccess: {
        background: isDark
          ? 'linear-gradient(145deg, #0a2218 0%, #071a12 100%)'
          : 'linear-gradient(145deg, #f0fdf6 0%, #e8f9f0 100%)',
        borderColor: isDark ? '#123d25' : '#9ddcbb',
        color: isDark ? '#4ade80' : '#166534',
      },
      standardError: {
        background: isDark
          ? 'linear-gradient(145deg, #2a0e0e 0%, #1f0909 100%)'
          : 'linear-gradient(145deg, #fef7f7 0%, #fef2f2 100%)',
        borderColor: isDark ? '#4a1515' : '#fca5a5',
        color: isDark ? '#f87171' : '#991b1b',
      },
      standardWarning: {
        background: isDark
          ? 'linear-gradient(145deg, #281a04 0%, #1e1302 100%)'
          : 'linear-gradient(145deg, #fffdf0 0%, #fffbeb 100%)',
        borderColor: isDark ? '#453008' : '#fcd34d',
        color: isDark ? '#fbbf24' : '#92400e',
      },
      standardInfo: {
        background: isDark
          ? 'linear-gradient(145deg, #0c1e38 0%, #091629 100%)'
          : 'linear-gradient(145deg, #f5f9ff 0%, #eff6ff 100%)',
        borderColor: isDark ? '#153155' : '#93c5fd',
        color: isDark ? '#60a5fa' : '#1e40af',
      },
    },
  },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createDialogOverrides = (isDark: boolean, c: any) => ({
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 12,
        border: `1px solid ${c.border}`,
        background: isDark
          ? 'linear-gradient(145deg, rgba(30,30,30,0.8) 0%, rgba(17,17,17,0.8) 100%)'
          : 'linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(245,245,245,0.9) 100%)',
        backdropFilter: 'blur(16px)',
        boxShadow: isDark
          ? `0 24px 64px rgba(0,0,0,1), ${c.glossStrong}`
          : `0 24px 64px rgba(0,0,0,0.15), 0 8px 24px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,1)`,
      },
    },
  },

  MuiDialogTitle: {
    styleOverrides: {
      root: {
        fontFamily: bodyFont,
        fontWeight: 600,
        fontSize: 15,
        borderBottom: `1px solid ${c.border}`,
        padding: '16px 20px',
      },
    },
  },

  MuiDialogContent: {
    styleOverrides: {
      root: { fontFamily: bodyFont, padding: '20px' },
    },
  },

  MuiDialogActions: {
    styleOverrides: {
      root: {
        borderTop: `1px solid ${c.border}`,
        padding: '12px 16px',
        gap: '8px',
      },
    },
  },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createWidgetOverrides = (isDark: boolean, c: any) => ({
  MuiAccordion: {
    styleOverrides: {
      root: {
        fontFamily: bodyFont,
        background: isDark
          ? 'linear-gradient(145deg, #161616 0%, #111111 100%)'
          : 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
        border: `1px solid ${c.border}`,
        borderRadius: '8px !important',
        boxShadow: 'none',
        marginBottom: 4,
        '&:before': { display: 'none' },
        '&.Mui-expanded': {
          boxShadow: isDark
            ? `0 4px 16px rgba(0,0,0,0.5), ${c.gloss}`
            : `0 4px 16px rgba(0,0,0,0.08), ${c.gloss}`,
        },
      },
    },
  },

  MuiAccordionSummary: {
    styleOverrides: {
      root: {
        fontFamily: bodyFont,
        fontWeight: 500,
        fontSize: 13,
        minHeight: 44,
        '&.Mui-expanded': { minHeight: 44 },
      },
      content: {
        '&.Mui-expanded': { margin: '12px 0' },
      },
    },
  },

  MuiAccordionDetails: {
    styleOverrides: {
      root: {
        fontFamily: bodyFont,
        fontSize: 13,
        borderTop: `1px solid ${c.border}`,
        padding: '12px 16px 16px',
      },
    },
  },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createBadgeOverrides = (isDark: boolean, brand: any) => ({
  MuiBadge: {
    styleOverrides: {
      badge: {
        fontFamily: bodyFont,
        fontSize: 10,
        fontWeight: 600,
        height: 18,
        minWidth: 18,
        padding: '0 4px',
        background: isDark
          ? `linear-gradient(145deg, ${brand.secondary} 0%, ${brand.secondary} 100%)`
          : `linear-gradient(145deg, ${brand.primary} 0%, ${brand.primary} 100%)`,
        color: '#ffffff',
        boxShadow: isDark
          ? `0 0 0 1.5px #000000, inset 0 1px 0 rgba(255,255,255,0.15)`
          : `0 0 0 1.5px #ffffff, inset 0 1px 0 rgba(255,255,255,0.2)`,
      },
    },
  },

  MuiSwitch: {
    styleOverrides: {
      root: { padding: 6 },
      track: {
        borderRadius: 99,
        backgroundColor: isDark ? '#333333' : '#d4d4d4',
        opacity: 1,
        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.3)',
      },
      thumb: {
        boxShadow: isDark
          ? '0 1px 4px rgba(0,0,0,0.6)'
          : '0 1px 4px rgba(0,0,0,0.2)',
        width: 16,
        height: 16,
      },
      switchBase: {
        '&.Mui-checked': {
          '& + .MuiSwitch-track': {
            backgroundColor: isDark ? brand.secondary : brand.primary,
            opacity: 1,
          },
        },
      },
    },
  },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createFormOverrides = (isDark: boolean, brand: any, c: any) => ({
  MuiSlider: {
    styleOverrides: {
      root: {
        color: isDark ? brand.secondary : brand.primary,
      },
      track: {
        background: isDark
          ? `linear-gradient(90deg, ${brand.secondary} 0%, ${brand.secondary} 100%)`
          : `linear-gradient(90deg, ${brand.primary} 0%, ${brand.primary} 100%)`,
        border: 'none',
      },
      rail: {
        backgroundColor: isDark ? '#2a2a2a' : '#e0e0e0',
        boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.2)',
      },
      thumb: {
        background: isDark ? '#ffffff' : brand.primary,
        border: `2px solid ${isDark ? '#cccccc' : brand.primary}`,
        boxShadow: isDark
          ? `0 1px 6px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.3)`
          : `0 1px 6px ${'rgba(163, 207, 204, 0.15)'}, inset 0 1px 0 rgba(255,255,255,0.4)`,
        '&:hover': {
          boxShadow: isDark
            ? `0 0 0 6px rgba(255,255,255,0.06)`
            : `0 0 0 6px ${'rgba(163, 207, 204, 0.15)'}`,
        },
      },
    },
  },

  MuiFormLabel: {
    styleOverrides: {
      root: {
        fontFamily: bodyFont,
        fontSize: 13,
        color: c.textSecondary,
        '&.Mui-focused': { color: isDark ? '#ffffff' : brand.primary },
      },
    },
  },

  MuiFormHelperText: {
    styleOverrides: {
      root: { fontFamily: bodyFont, fontSize: 11, marginTop: 4 },
    },
  },

  MuiTypography: {
    styleOverrides: {
      root: { fontFamily: bodyFont },
    },
  },

  MuiListItemText: {
    styleOverrides: {
      primary: { fontFamily: bodyFont, fontSize: 13, color: 'inherit' },
      secondary: {
        fontFamily: bodyFont,
        fontSize: 12,
        color: c.textSecondary,
      },
    },
  },

  MuiListItemButton: {
    styleOverrides: {
      root: {
        fontFamily: bodyFont,
        borderRadius: 6,
        margin: '2px 8px',
        color: c.textSecondary,
        transition: 'all 0.2s ease',
        '&:hover': { backgroundColor: c.bgHover, color: c.textPrimary },
        '&.Mui-selected': {
          background: isDark
            ? `linear-gradient(135deg, rgba(166, 200, 255, 0.15) 0%, rgba(65, 151, 255, 0.05) 100%)`
            : `linear-gradient(145deg, rgba(0,49,48,0.08) 0%, rgba(0,49,48,0.02) 100%)`,
          color: brand.primary,
          fontWeight: 600,
          boxShadow: isDark
            ? 'inset 0 1px 0 rgba(255,255,255,0.05)'
            : 'inset 0 1px 0 rgba(255,255,255,0.85)',
          '&:hover': {
            background: isDark
              ? `linear-gradient(145deg, rgba(125,219,126,0.15) 0%, rgba(125,219,126,0.05) 100%)`
              : `linear-gradient(145deg, rgba(0,49,48,0.12) 0%, rgba(0,49,48,0.04) 100%)`,
          },
        },
      },
    },
  },
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const createNavOverrides = (isDark: boolean, brand: any, c: any) => ({
  MuiDrawer: {
    styleOverrides: {
      paper: {
        fontFamily: bodyFont,
        backgroundColor: isDark
          ? 'rgba(50, 53, 57, 0.6)'
          : 'rgba(255,255,255,0.85)',
        backdropFilter: 'blur(40px)',
        border: 'none',
        borderRight: 'none',
        boxShadow: isDark
          ? '2px 0 24px rgba(0, 0, 0, 0.4)'
          : '2px 0 24px rgba(0, 0, 0, 0.05)',
      },
    },
  },

  MuiAppBar: {
    styleOverrides: {
      root: {
        fontFamily: bodyFont,
        background: isDark
          ? 'linear-gradient(145deg, rgba(0,23,22,0.8) 0%, rgba(0,20,19,0.8) 100%)'
          : 'linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(250,250,250,0.9) 100%)',
        backdropFilter: 'blur(40px)',
        borderBottom: 'none',
        boxShadow: isDark
          ? `0 4px 20px rgba(0,0,0,0.4)`
          : `0 4px 16px rgba(0,0,0,0.06)`,
        color: c.textPrimary,
      },
    },
  },

  MuiToolbar: {
    styleOverrides: {
      root: { fontFamily: bodyFont },
    },
  },

  MuiSnackbar: {
    styleOverrides: {
      root: { fontFamily: bodyFont },
    },
  },

  MuiSnackbarContent: {
    styleOverrides: {
      root: {
        fontFamily: bodyFont,
        fontSize: 13,
        backgroundColor: isDark ? '#2a2a2a' : '#111111',
        border: `1px solid ${isDark ? '#3a3a3a' : 'transparent'}`,
        borderRadius: 8,
        color: '#ffffff',
        boxShadow: isDark
          ? `0 8px 24px rgba(0,0,0,0.8), ${c.glossStrong}`
          : `0 8px 24px rgba(0,0,0,0.2), ${c.glossStrong}`,
      },
    },
  },

  MuiPopover: {
    styleOverrides: {
      paper: {
        borderRadius: 9,
        border: `1px solid ${c.border}`,
        background: isDark
          ? 'linear-gradient(145deg, #181818 0%, #111111 100%)'
          : 'linear-gradient(145deg, #ffffff 0%, #fafafa 100%)',
        boxShadow: isDark
          ? `0 8px 32px rgba(0,0,0,0.8), ${c.glossStrong}`
          : `0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06), ${c.glossStrong}`,
      },
    },
  },

  MuiSkeleton: {
    styleOverrides: {
      root: {
        backgroundColor: isDark ? '#1a1a1a' : '#f0f0f0',
        '&::after': {
          background: isDark
            ? 'linear-gradient(90deg, transparent, rgba(255,255,255,0.03), transparent)'
            : 'linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)',
        },
      },
    },
  },
});

export default createAppTheme;
